import React from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Typography, Input, IconButton } from '@mui/joy';
import { observer } from 'mobx-react-lite';

import { missionPlannerStore } from '../../stores/MissionPlannerStore';
import { SimpleItem } from '../../types/Plan';

const WaypointSettings: React.FC = () => {
  const { selectedIndex, missionItems } = missionPlannerStore;

  if (selectedIndex === null) return null;

  const selectedItem = missionItems[selectedIndex];
  if (!selectedItem || selectedItem.type !== 'SimpleItem' || selectedItem.command !== 16) {
    return null; // Only show for selected waypoints
  }

  const waypointItem = selectedItem as SimpleItem;

  const handleParamChange = (paramIndex: number, value: string) => {
    const newValue = parseFloat(value);
    if (!Number.isNaN(newValue)) {
      const newParams = [...waypointItem.params] as SimpleItem['params'];
      newParams[paramIndex] = newValue;
      if (paramIndex === 6) {
        // Altitude
        const newCoordinate = [...waypointItem.coordinate] as SimpleItem['coordinate'];
        newCoordinate[2] = newValue;
        missionPlannerStore.updateMissionItem(selectedIndex, { params: newParams, coordinate: newCoordinate });
      } else {
        missionPlannerStore.updateMissionItem(selectedIndex, { params: newParams });
      }
    }
  };

  const handleDelete = () => {
    missionPlannerStore.removeMissionItem(selectedIndex);
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (selectedIndex !== null) {
      missionPlannerStore.updateMissionItem(selectedIndex, { name: event.target.value });
    }
  };

  return (
    <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 'md' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography level='title-md'>Waypoint</Typography>
        <IconButton size='sm' variant='plain' color='danger' onClick={handleDelete}>
          <DeleteIcon />
        </IconButton>
      </Box>
      <Typography level='body-sm' sx={{ mb: 2 }}>
        Travel to a position in 3D space.
      </Typography>

      <Typography level='body-sm'>Name</Typography>
      <Input placeholder='Waypoint Name' value={waypointItem.name || ''} onChange={handleNameChange} sx={{ mb: 2 }} />

      <Typography level='body-sm'>Altitude</Typography>
      <Input
        type='number'
        value={waypointItem.params[6] || ''}
        onChange={(e) => handleParamChange(6, e.target.value)}
        endDecorator={<Typography level='body-xs'>ft</Typography>}
        sx={{ mb: 2 }}
      />

      <Typography level='body-sm'>Hold</Typography>
      <Input
        type='number'
        value={waypointItem.params[0] || ''}
        onChange={(e) => handleParamChange(0, e.target.value)}
        endDecorator={<Typography level='body-xs'>secs</Typography>}
        sx={{ mb: 2 }}
      />

      <Typography level='body-sm'>Yaw</Typography>
      <Input
        type='number'
        placeholder='--.--'
        value={waypointItem.params[3] || ''}
        onChange={(e) => handleParamChange(3, e.target.value)}
        endDecorator={<Typography level='body-xs'>deg</Typography>}
        sx={{ mb: 2 }}
      />

      <Typography level='body-sm'>Latitude</Typography>
      <Input readOnly value={waypointItem.coordinate[0].toFixed(6)} sx={{ mb: 2 }} />

      <Typography level='body-sm'>Longitude</Typography>
      <Input readOnly value={waypointItem.coordinate[1].toFixed(6)} />
    </Box>
  );
};

export default observer(WaypointSettings);
